import { HTTPStatus } from '~/providers/new/httpStatus'
import { IFlash } from '~/interfaces/flashInterfaces'
import { TOrFunction } from '~/interfaces/generalInterfaces'
import store from '~/state/store'
import jqXHR = JQuery.jqXHR

interface IResult {
  success: boolean
}

type TOrNull<T> = T | null
type TSetFlash = (flash: IFlash) => void
type TSuccessFunction<R> = (payload: { result: R, setFlash: TSetFlash }) => void
type TErrorResult = Error | jqXHR
type TResultOrError<R, E> = { result: R } | { error: E }
type TErrorFunction<R, E> = (payload: TResultOrError<R, E> & { message: string, onErrorDefault: Omit<TErrorFunction<R, E>, 'onErrorDefault'>, setFlash: TSetFlash }) => void

/**
 * Convenience class that wraps the try/catch/finally statement
 * with some added convention defaults for Formability:
 * - setFlash on error
 * - attach descriptions to error messages for common error types (e.g. Action is forbidden)
 */
export default class Try {
  /**
   * The return of the +onSuccess+ function must be an object with {success: boolean} for +Try+ to determine status
   * of the +action+
   *
   * Pass in +null+ for any callback to prevent the default function from being called
   * @example
   *   Try.call(async () => (await DataTagProvider.fetch())[0],
   *     {
   *       onSuccess: ({ result }) => {
   *         this.allDataTags = result.data_tags
   *         this.filteredDataTags = this.allDataTags
   *       },
   *       onFailure: null, // don't want onFailure default function to run
   *       errorMessage: 'Something went wrong',
   *       // not passing in anything for onError means default function will run
   *       onFinally: () => this.fetchingBusy = false,
   *     }
   *   )
   *
   * @example Override default on error callback but still use +onErrorDefault+
   *   Try.call(async () => (await DataTagProvider.fetch())[0],
   *     {
   *       onSuccess: ({ result }) => { ... },
   *       onError: (payload) => {
   *         payload.onErrorDefault(payload)
   *         this.close()
   *       },
   *     }
   *   )
   *
   * @param action
   * @param opts
   */
  static async call<R extends IResult, E extends TErrorResult>(
      action: () => R | Promise<R>,
      opts: {
        errorMessage?: TOrFunction<string, E>,
        onError?: TOrNull<TErrorFunction<R, E>>,
        onFailure?: TOrNull<TErrorFunction<R, E>>,
        onFinally?: TOrNull<() => void>,
        onSuccess?: TOrNull<TSuccessFunction<R>>,
      } = {}) {
    const { onSuccess, onError = onErrorDefault, onFailure = onError, onFinally } = opts

    try {
      const result = await action()
      if (result?.success) {
        onSuccess?.({ result, setFlash })
      } else {
        onFailure?.({ result, message: errorMessage(get(opts.errorMessage)), onErrorDefault, setFlash })
      }
    } catch (error) {
      console.error(error);
      onError?.({ error, message: errorMessage(get(opts.errorMessage, error), error), onErrorDefault, setFlash })
    } finally {
      onFinally?.()
    }
  }
}

function isFunction<V>(value: unknown): value is (...args: unknown[]) => V {
  return typeof value === 'function'
}

function get(value: any, ...args: unknown[]) {
  return isFunction(value) ? value(...args) : value
}

function setFlash(flash: IFlash) {
  store.commit('flashStore/set', { flash })
}

function errorMessage(message: string, error?: TErrorResult): string {
  if (!error || !('status' in error)) return message

  const messages = [message]
  switch (error.status) {
    case HTTPStatus.Forbidden:
      messages.push('Action is forbidden.')
      break
    case HTTPStatus.Unauthorized:
      messages.push('Action is unauthorized.')
      break
  }

  return messages.join(' ')
}

function onErrorDefault<R extends IResult, E extends TErrorResult>(payload: Parameters<TErrorFunction<R, E>>[0]) {
  setFlash({ alert: payload.message })
}
